import type { AlertsStreamFrame } from '../../alert-kinds/index.js';
import type { NotificationsResponse } from '../interfaces/INotificationsApi.js';

/**
 * The frames `/api/notifications/stream` sends: the signal, and the session's
 * own keep-alive. A signal says the person's notifications changed, and in
 * which site — never what they are. One without a site means "ask again
 * whatever site is showing": the server does not know what was missed.
 */
export type NotificationsStreamEvent =
  | { channel: 'notifications.changed'; data: { siteId?: string } }
  | { channel: '_heartbeat'; data: { ts: number } }
  // The site's stream also relays the person's alerts in it (alerts spec, D2).
  | AlertsStreamFrame;

import { handle } from './errors.js';
import type { AuthedFetch } from './transport.js';

/**
 * The notifications API over an injected transport — each app binds its own
 * `authedFetch` in its `apis/notifications.ts`, so the contract lives once.
 */
export function createNotificationsApi(authedFetch: AuthedFetch) {
  /** What is waiting for the signed-in person in the site, in display order. */
  async function fetchNotifications(siteId: string, signal?: AbortSignal): Promise<NotificationsResponse> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/notifications`, { signal });
    if (!res.ok) return handle(res);
    return res.json() as Promise<NotificationsResponse>;
  }

  const alertPath = (siteId: string, rest: string) => `/api/sites/${encodeURIComponent(siteId)}/alerts${rest}`;

  async function post(path: string): Promise<void> {
    const res = await authedFetch(path, { method: 'POST' });
    if (!res.ok) return handle(res);
  }

  /** The person opened an alert: it is viewed, here and in every app on the engine. */
  function markAlertViewed(siteId: string, alertId: string): Promise<void> {
    return post(alertPath(siteId, `/${encodeURIComponent(alertId)}/viewed`));
  }

  /** The person dismissed an alert: it leaves their list. */
  function dismissAlert(siteId: string, alertId: string): Promise<void> {
    return post(alertPath(siteId, `/${encodeURIComponent(alertId)}/dismiss`));
  }

  /** Every alert of the person's in the site is viewed. */
  function markAllAlertsViewed(siteId: string): Promise<void> {
    return post(alertPath(siteId, '/viewed'));
  }

  return { fetchNotifications, markAlertViewed, dismissAlert, markAllAlertsViewed };
}
